import React, { useEffect, useState } from "react";

import { notifications } from "@mantine/notifications";
import { IconSearch, IconX } from "@tabler/icons-react";
import { TextInput } from "@mantine/core";
import {
  Button,
  Modal,
  ModalActions,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalTitle,
} from "@onengine/component-library";

import { useFileManager, useFileManagerUI } from "@/components/core/FileManager";
import { modalBody } from "@/components/features/FileManager/Modals/modals.style.ts";
import { isSystemTag } from "@/components/features/FileManager/fileTags.utils";
import { useFileTagDropdown } from "@/api/documents/fileTags.queries";
import { useDebouncedValue } from "@mantine/hooks";

import styles from "./TagsModal.module.scss";

const tagsSearchContainer: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 4,
};
const tagsHint: React.CSSProperties = {
  margin: 0,
  fontSize: 12,
  color: "var(--text-default, #363d4d)",
  lineHeight: "16px",
};
const tagsSection: React.CSSProperties = { display: "flex", flexDirection: "column", gap: 16 };
const tagsSectionHeader: React.CSSProperties = { display: "flex", flexDirection: "column", gap: 4 };
const tagsSectionTitle: React.CSSProperties = {
  margin: 0,
  fontSize: 16,
  fontWeight: 700,
  color: "var(--text-strong, #131927)",
  lineHeight: "20px",
};
const tagsSectionDesc: React.CSSProperties = {
  margin: 0,
  fontSize: 14,
  color: "var(--text-weak, #646d7e)",
  lineHeight: "20px",
};
const tagsWrap: React.CSSProperties = { display: "flex", flexWrap: "wrap", gap: 8 };
const tagChipBase: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  height: 32,
  paddingInline: 8,
  gap: 2,
  border: "1px solid var(--border-default, #cfd4dd)",
  borderRadius: 120,
  background: "var(--bg-neutral-default, white)",
  overflow: "hidden",
};
const tagChipText: React.CSSProperties = {
  fontSize: 14,
  color: "var(--text-strong, #131927)",
  lineHeight: "20px",
  paddingInline: 4,
};
const tagChipRemoveBtn: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 16,
  height: 16,
  background: "none",
  border: "none",
  cursor: "pointer",
  padding: 0,
  flexShrink: 0,
  color: "var(--text-default, #363d4d)",
};
const addSuggestionChip: React.CSSProperties = {
  ...tagChipBase,
  cursor: "pointer",
  borderStyle: "dashed",
  background: "none",
};

export function TagsModal() {
  const { updateTags } = useFileManager();
  const { tagsTarget, closeTags } = useFileManagerUI();
  const [localTags, setLocalTags] = useState<string[]>([]);
  const [searchValue, setSearchValue] = useState("");

  useEffect(() => {
    if (tagsTarget) {
      setLocalTags(tagsTarget.tags.filter((t) => !isSystemTag(t)));
      setSearchValue("");
    }
  }, [tagsTarget?.id]);

  const trimmed = searchValue.trim();
  const canAdd = trimmed.length > 0 && !localTags.includes(trimmed);
  const [debouncedSearch] = useDebouncedValue(trimmed, 300);
  const { data: tagOptions = [] } = useFileTagDropdown(debouncedSearch);
  const suggestions = tagOptions
    .map((option) => option.name)
    .filter((name) => name !== trimmed && !localTags.includes(name) && !isSystemTag(name))
    .slice(0, 8);

  function addTag(tag: string) {
    const t = tag.trim();
    if (!t || localTags.includes(t) || isSystemTag(t)) return;
    setLocalTags((prev) => [...prev, t]);
    setSearchValue("");
  }

  function removeTag(tag: string) {
    setLocalTags((prev) => prev.filter((t) => t !== tag));
  }

  function handleUpdate() {
    if (!tagsTarget) return;
    const systemTags = tagsTarget.tags.filter(isSystemTag);
    updateTags(
      { id: tagsTarget.id, name: tagsTarget.name, tags: [...systemTags, ...localTags] },
      {
        onSuccess: closeTags,
        onError: () => {
          notifications.show({
            title: "Update failed",
            message: "Could not update tags. Please try again.",
            color: "red",
            autoClose: 4000,
          });
        },
      },
    );
  }

  return (
    <Modal open={!!tagsTarget} onClose={closeTags}>
      <ModalContent>
        <ModalTitle>Modify Tags</ModalTitle>
        <ModalBody>
          <div style={{ ...modalBody, gap: 24 }}>
            <div style={tagsSearchContainer}>
              <TextInput
                placeholder="Search.."
                value={searchValue}
                leftSection={<IconSearch size={16} />}
                onChange={(e) => setSearchValue(e.currentTarget.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addTag(searchValue);
                  }
                }}
              />
              {(canAdd || suggestions.length > 0) && (
                <div style={tagsWrap}>
                  {canAdd && (
                    <button
                      type="button"
                      style={addSuggestionChip}
                      onClick={() => addTag(searchValue)}
                    >
                      <span style={tagChipText}>+ {trimmed}</span>
                    </button>
                  )}
                  {suggestions.map((name) => (
                    <button
                      key={name}
                      type="button"
                      style={addSuggestionChip}
                      onClick={() => addTag(name)}
                    >
                      <span style={tagChipText}>+ {name}</span>
                    </button>
                  ))}
                </div>
              )}
              <p style={tagsHint}>Search for a tag and click to add it to your list</p>
            </div>

            <div style={tagsSection}>
              <div style={tagsSectionHeader}>
                <p style={tagsSectionTitle}>Tags</p>
                <p style={tagsSectionDesc}>
                  Tags linked to this File. You can remove or add more tags as needed.
                </p>
              </div>
              {localTags.length > 0 ? (
                <div style={tagsWrap}>
                  {localTags.map((tag) => (
                    <div key={tag} style={tagChipBase}>
                      <span style={tagChipText}>{tag}</span>
                      <button
                        type="button"
                        style={tagChipRemoveBtn}
                        onClick={() => removeTag(tag)}
                        aria-label={`Remove ${tag}`}
                      >
                        <IconX size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className={styles.emptyState}>No tags yet. Search above to add tags.</p>
              )}
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <ModalActions position={"right"}>
            <Button variant="neutral-secondary" onClick={closeTags}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleUpdate}>
              Update Tags
            </Button>
          </ModalActions>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
